import React from 'react';

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';

import {

  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,

} from 'recharts';



import { MONO_FONT, TOKENS } from '../../../theme/muiTheme';

import { formatNumber, formatTonne } from '../../../shared/utils/number';

import { CLIMATE_ACTION_COLORS } from '../model/climateActionColors';



const valueOrNull = value => (Number.isFinite(Number(value)) ? Number(value) : null);



function PathwayTooltip({ active, payload, label, t }) {

  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload || {};

  const actualLabel = t('climateAction.pathway.actual', { defaultValue: 'Actual total' });

  const targetLabel = t('climateAction.pathway.target', { defaultValue: 'Annual target' });

  return (

    <Paper elevation={4} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}>

      <Stack spacing={0.55}>

        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>

        <Box sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, mt: 0.35, pt: 0.55 }}>

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>

            <Typography variant="caption" sx={{ fontWeight: 700 }}>{actualLabel}</Typography>

            <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 700 }}>

              {row.actual === null ? '—' : formatTonne(row.actual, { withUnit: true })}

            </Typography>

          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 0.45 }}>

            <Typography variant="caption" color="text.secondary">{targetLabel}</Typography>

            <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontWeight: 650 }}>

              {row.target === null ? '—' : formatTonne(row.target, { withUnit: true })}

            </Typography>

          </Stack>

          {row.actual !== null && row.target !== null && (

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mt: 0.45 }}>

              <Typography variant="caption" color="text.secondary">

                {t('climateAction.pathway.tooltipVariance', { defaultValue: 'Variance' })}

              </Typography>

              <Typography variant="caption" sx={{ color: row.actual - row.target > 0 ? TOKENS.warning : TOKENS.success, fontFamily: MONO_FONT, fontWeight: 700 }}>

                {row.actual - row.target > 0 ? '+' : ''}{formatTonne(row.actual - row.target, { withUnit: true })}

              </Typography>

            </Stack>

          )}

        </Box>

      </Stack>

    </Paper>

  );

}

const formatAxis = value => (

  Math.abs(value) >= 1000

    ? `${formatNumber(value / 1000, { max: 1 })}k`

    : formatNumber(value, { max: 1 })

);



/** Compare total actual inventory with the committed annual target pathway. */

export default function TargetPathwayChart({

  pathway = [], selectedYear = 2025, t, isHistoricalTarget = false,

}) {

  const theme = useTheme();

  const cycle = pathway.length > 0

    ? {

      id: 'api-pathway',

      baseYear: Number(pathway[0].fiscal_year),

      targetYear: Number(pathway[pathway.length - 1].fiscal_year),

      status: 'ACTIVE',

    }

    : null;

  const series = pathway.map(row => ({

    year: Number(row.fiscal_year),

    actual: valueOrNull(row.actual_tco2e),

    target: valueOrNull(row.target_tco2e),

  }));

  return (

    <Stack spacing={1.25} sx={{ height: '100%' }}>

      {cycle && (

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5}>

          {isHistoricalTarget && <Chip color={'warning'} label={t('climateAction.kpi.historicalTarget')} size={'small'} sx={{ height: 20, '& .MuiChip-label': { fontSize: 10.5, fontWeight: 700, px: 1 } }} />}

        </Stack>

      )}

      <Box role="img" aria-label={t('climateAction.pathway.ariaLabel')} sx={{ height: 310, minWidth: 0 }}>

        <ResponsiveContainer width="100%" height="100%">

          <BarChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>

            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />

            <XAxis

              dataKey="year"

              axisLine={{ stroke: theme.palette.divider }}

              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}

              tickLine={false}

            />

            <YAxis

              axisLine={false}

              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}

              tickFormatter={formatAxis}

              tickLine={false}

              width={58}

            />

            <Tooltip content={<PathwayTooltip t={t} />} cursor={{ fill: alpha(CLIMATE_ACTION_COLORS.target, 0.07) }} />

            <Legend wrapperStyle={{ fontSize: 12 }} />

            <Bar

              dataKey="actual"

              fill={theme.palette.mode === 'dark' ? CLIMATE_ACTION_COLORS.inventoryDark : CLIMATE_ACTION_COLORS.inventory}

              maxBarSize={34}

              name={t('climateAction.pathway.actual', { defaultValue: 'Actual total' })}

              radius={[4, 4, 0, 0]}

            />

            <Bar

              dataKey="target"

              fill={theme.palette.mode === 'dark' ? CLIMATE_ACTION_COLORS.targetDark : CLIMATE_ACTION_COLORS.target}

              maxBarSize={34}

              name={t('climateAction.pathway.target', { defaultValue: 'Annual target' })}

              stroke={theme.palette.mode === 'dark' ? '#fdba74' : '#c2410c'}

              strokeWidth={1}

              radius={[4, 4, 0, 0]}

            />

          </BarChart>

        </ResponsiveContainer>

      </Box>

    </Stack>

  );

}